import { uniq } from "lodash-es";

import {
  ChatRouteStopEntityType,
  ChatRouteStopTarget,
} from "@mapmycustomers/shared/types/chat/ChatContent";

import { parseApiDate } from "@app/util/parsers";

export type CreateRouteStopKey = Pick<ChatRouteStopTarget, "entityId" | "entityType">;

// The card's stops come from the priced preview when there is one, whose identity is nullable, or
// from the proposal's own list, whose identity never is.
interface IdentifiedStop {
  entityId: null | number;
  entityType: ChatRouteStopEntityType | null;
}

const isSameStop = (stop: IdentifiedStop, key: CreateRouteStopKey) =>
  stop.entityId === key.entityId && stop.entityType === key.entityType;

// The rep's order, re-checked against the stops on the card: a key naming no stop, or one named
// twice, is dropped and a stop the order misses keeps its place after the ones it names, so a
// stored order can neither lose a stop nor add one.
export const orderCreateRouteStops = <T extends IdentifiedStop>(
  stops: T[],
  order: CreateRouteStopKey[] | undefined
): T[] => {
  if (!order) {
    return stops;
  }
  const ordered = uniq(order.flatMap((key) => stops.filter((stop) => isSameStop(stop, key))));
  return [...ordered, ...stops.filter((stop) => !ordered.includes(stop))];
};

// The order the platform reads as stopOrder: every proposed stop exactly once, which it requires.
// Undefined when the stored order names none of them — that is no move at all.
export const normalizeCreateRouteStopOrder = (
  stops: ChatRouteStopTarget[],
  order: CreateRouteStopKey[] | undefined
): CreateRouteStopKey[] | undefined =>
  order?.some((key) => stops.some((stop) => isSameStop(stop, key)))
    ? orderCreateRouteStops(stops, order).map(({ entityId, entityType }) => ({
        entityId,
        entityType,
      }))
    : undefined;

// Swaps the stop at `index` with its neighbour, the way mobile's ▲/▼ do, and returns the whole
// order by record — what the draft keeps and the platform reads as stopOrder.
export const moveCreateRouteStop = (
  stops: IdentifiedStop[],
  index: number,
  direction: -1 | 1
): CreateRouteStopKey[] => {
  const target = index + direction;
  const moved = [...stops];
  if (target >= 0 && target < stops.length) {
    [moved[index], moved[target]] = [moved[target], moved[index]];
  }
  return moved.flatMap(({ entityId, entityType }) =>
    entityId !== null && entityType !== null ? [{ entityId, entityType }] : []
  );
};

// A preview only: confirming re-plans the route and can change every arrival. Moves an arrival by
// the same amount the start moved; anything unreadable keeps the time it had.
export const shiftArrivalTime = (
  arrivalTime: null | string,
  proposedStartAt: string,
  startAt: string
): null | string => {
  const arrivalMs = arrivalTime ? parseApiDate(arrivalTime).getTime() : NaN;
  const deltaMs = parseApiDate(startAt).getTime() - parseApiDate(proposedStartAt).getTime();
  return Number.isNaN(arrivalMs) || Number.isNaN(deltaMs)
    ? arrivalTime
    : new Date(arrivalMs + deltaMs).toISOString();
};
